import React from 'react';
import * as Icons from 'lucide-react';
import { useAmenity } from '../contexts/AmenitiesContext.jsx';

/**
 * Renders a single amenity as an icon + label badge.
 * Uses the canonical amenities lookup from the API.
 */
export function AmenityTag({ 
  amenityKey, 
  compact = false,
  showIcon = true,
  showLabel = true,
  className = '',
  iconClassName = '',
  labelClassName = ''
}) {
  const amenity = useAmenity(amenityKey);
  
  // Fallback if amenity not found in lookup
  if (!amenity) {
    const fallbackLabel = amenityKey
      .replace(/_/g, ' ')
      .replace(/\b\w/g, l => l.toUpperCase());
    
    if (compact) {
      return (
        <span className={`inline-flex items-center gap-1 text-xs text-gray-600 dark:text-gray-400 ${className}`}>
          {showIcon && <span className="w-3 h-3">•</span>}
          {showLabel && <span>{fallbackLabel}</span>}
        </span>
      );
    }
    
    return (
      <span className={`inline-flex items-center gap-2 px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 text-sm rounded-full ${className}`}>
        {showIcon && <span className="w-3 h-3">•</span>}
        {showLabel && <span>{fallbackLabel}</span>}
      </span>
    );
  }
  
  // Get the icon component from lucide-react
  const IconComponent = Icons[amenity.icon_name] || Icons.Home;
  
  // Compact mode (small text with icon)
  if (compact) {
    return (
      <span className={`inline-flex items-center gap-1 text-xs text-gray-600 dark:text-gray-400 ${className}`}>
        {showIcon && (
          <IconComponent className={`w-3 h-3 flex-shrink-0 ${iconClassName}`} />
        )}
        {showLabel && <span className={labelClassName}>{amenity.label}</span>}
      </span>
    );
  }
  
  // Default mode (badge with icon + label)
  return (
    <span className={`inline-flex items-center gap-2 px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 text-sm rounded-full ${className}`}>
      {showIcon && (
        <IconComponent className={`w-4 h-4 flex-shrink-0 text-blue-600 ${iconClassName}`} />
      )}
      {showLabel && <span className={labelClassName}>{amenity.label}</span>}
    </span>
  );
}

/**
 * Renders a list of amenities as a grid or flex list.
 * Uses the canonical amenities lookup from the API.
 */
export function AmenityList({ 
  amenityKeys = [], 
  maxItems = null,
  compact = false,
  showIcons = true,
  className = '',
  itemClassName = ''
}) {
  if (!Array.isArray(amenityKeys) || amenityKeys.length === 0) {
    return (
      <div className={`text-gray-500 text-sm ${className}`}>
        No amenities listed
      </div>
    );
  }
  
  const itemsToShow = maxItems ? amenityKeys.slice(0, maxItems) : amenityKeys;
  const remainingCount = maxItems ? Math.max(0, amenityKeys.length - maxItems) : 0;
  
  return (
    <div className={`grid grid-cols-1 sm:grid-cols-2 gap-2 ${className}`}>
      {itemsToShow.map((key, index) => (
        <AmenityTag
          key={key}
          amenityKey={key}
          compact={compact}
          showIcon={showIcons}
          className={itemClassName}
        />
      ))}
      {remainingCount > 0 && (
        <span className="text-xs text-gray-600 dark:text-gray-400 py-0.5">
          +{remainingCount} more
        </span>
      )}
    </div>
  );
}

import { useAmenities } from '../contexts/AmenitiesContext.jsx';

/**
 * Amenity filter component - renders grouped checkboxes for filtering
 * Uses the canonical amenities lookup from the API.
 */
export function AmenityFilter({
  selectedKeys = [],
  onChange,
  className = ''
}) {
  const { amenities, loading, getGroupedAmenities } = useAmenities();

  // Memoize the grouped amenities to prevent infinite re-renders
  const grouped = React.useMemo(() => {
    return getGroupedAmenities();
  }, [getGroupedAmenities]);

  const toggleAmenity = (key) => {
    if (selectedKeys.includes(key)) {
      onChange(selectedKeys.filter(k => k !== key));
    } else {
      onChange([...selectedKeys, key]);
    }
  };

  if (loading) {
    return (
      <div className={`text-sm text-gray-500 dark:text-gray-400 ${className}`}>
        Loading amenities...
      </div>
    );
  }

  if (!amenities || amenities.length === 0) {
    return (
      <div className={`text-sm text-gray-500 dark:text-gray-400 ${className}`}>
        No amenities available
      </div>
    );
  }

  return (
    <div className={`space-y-4 ${className}`}>
      {Object.entries(grouped).map(([category, categoryAmenities]) => (
        <div key={category}>
          <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2 capitalize">
            {category.replace(/_/g, ' ')}
          </h4>
          <div className="space-y-2">
            {categoryAmenities?.map?.(amenity => (
              <label key={amenity?.key} className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={selectedKeys.includes(amenity?.key)}
                  onChange={() => toggleAmenity(amenity?.key)}
                  className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">{amenity?.label}</span>
              </label>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
